import {
  createContext,
  useContext,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";

export type EquipmentDraftCache = Map<string, Map<string, unknown>>;
const DraftContext = createContext<Map<string, unknown> | null>(null);

// Keep in-memory drafts while Back/Forward changes the current URL. Files and
// signatures stay in memory; no evidence is copied to localStorage or history.
export function EquipmentDraft({
  cache,
  name,
  children,
}: {
  cache: EquipmentDraftCache;
  name: string;
  children: ReactNode;
}) {
  const [draft] = useState(() => {
    const existing = cache.get(name);
    if (existing) return existing;
    const created = new Map<string, unknown>();
    cache.set(name, created);
    return created;
  });
  return (
    <DraftContext.Provider value={draft}>{children}</DraftContext.Provider>
  );
}

export function useEquipmentDraftState<T>(
  key: string,
  initial: T | (() => T),
): [T, Dispatch<SetStateAction<T>>];
export function useEquipmentDraftState<T>(
  key: string,
): [T | undefined, Dispatch<SetStateAction<T | undefined>>];
export function useEquipmentDraftState<T>(
  key: string,
  initial?: T | (() => T),
) {
  const draft = useContext(DraftContext);
  const [value, setValue] = useState<T | undefined>(() =>
    draft?.has(key)
      ? (draft.get(key) as T)
      : typeof initial === "function"
        ? (initial as () => T)()
        : initial,
  );
  const update: Dispatch<SetStateAction<T | undefined>> = (next) => {
    setValue((previous) => {
      const result =
        typeof next === "function"
          ? (next as (value: T | undefined) => T | undefined)(previous)
          : next;
      draft?.set(key, result);
      return result;
    });
  };
  return [value, update] as const;
}
